import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import { AppHeader } from "@/components/brand/AppHeader";
import { MobileMenu } from "@/components/AppNav";
import { Button } from "@/components/ui/button";
import { groupOverview, errText } from "@/lib/bidCenterApi";

const ST = { queued: "Queued", ready: "Ready", posted: "Posted", pending_approval: "Pending approval", failed: "Failed", skipped: "Skipped" };
const SC = { queued: "bg-stone-100", ready: "bg-blue-100 text-blue-800", posted: "bg-emerald-100 text-emerald-800", pending_approval: "bg-[#FEF3C7] text-[#92400E]", failed: "bg-red-100 text-red-800", skipped: "bg-stone-200 text-stone-600" };

function HorseRow({ h, onOpen }) {
  return (
    <div data-testid={`overview-horse-${h.horse_id}`} className="rounded-xl border bg-white p-4 space-y-2">
      <div className="flex justify-between gap-2 items-start">
        <div>
          <p className="font-semibold">{h.horse_name || "Unnamed horse"}</p>
          {h.auction_post_url ? <a href={h.auction_post_url} target="_blank" rel="noreferrer" className="text-xs text-[#1877F2] inline-flex items-center gap-1"><ExternalLink className="h-3 w-3" />Auction post</a>
            : <span className="text-xs text-[#B45309]">Auction post not published yet</span>}
        </div>
        <Button size="sm" variant="outline" data-testid={`overview-open-${h.horse_id}`} onClick={onOpen} className="h-10 sm:h-8 shrink-0">Open Publishing</Button>
      </div>
      <div className="flex flex-wrap gap-1 text-[11px]">{Object.entries(h.counts).filter(([, n]) => n).map(([k, n]) => <span key={k} className={`rounded-full px-2 py-0.5 font-semibold ${SC[k]}`}>{ST[k]} {n}</span>)}
        {!h.total && <span className="text-stone-500">No groups assigned</span>}</div>
      {h.groups.length > 0 && <ul className="text-xs divide-y">{h.groups.map((g) => (
        <li key={g.id} data-testid={`overview-row-${g.id}`} className="py-1.5 flex flex-wrap justify-between gap-2">
          <span className="min-w-0 break-words">{g.group_name}</span>
          <span className="flex items-center gap-2"><span className={`rounded-full px-2 py-0.5 font-semibold ${SC[g.status]}`}>{ST[g.status]}</span>
            {g.posted_url && <a href={g.posted_url} target="_blank" rel="noreferrer" className="text-[#1877F2]">post</a>}</span>
        </li>))}</ul>}
    </div>
  );
}

export default function GroupOverview() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  useEffect(() => { groupOverview().then(setRows).catch((e) => toast.error(errText(e))); }, []);
  return (
    <div className="min-h-screen bg-stone-50">
      <AppHeader title="Group Distribution Overview" onBack={() => navigate("/groups")} backTestId="overview-back" width="max-w-5xl"><MobileMenu /></AppHeader>
      <main className="max-w-5xl mx-auto px-3 sm:px-6 py-4 space-y-3" data-testid="group-overview">
        <p className="text-sm text-stone-600">Read-only. Plan and update shares in each horse's Workspace → Publishing tab.</p>
        {rows === null && <p className="text-sm text-stone-500">Loading…</p>}
        {rows?.length === 0 && <p className="text-sm text-stone-500" data-testid="overview-empty">No horses with an auction post or group plan yet.</p>}
        {rows?.map((h) => <HorseRow key={h.horse_id} h={h} onOpen={() => navigate(`/pipeline?mode=all&h=${encodeURIComponent(h.pipeline_key)}&ws=publishing`)} />)}
      </main>
    </div>
  );
}
